import {
  json,
  type ActionFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { Form, Link, useActionData, useNavigation } from "@remix-run/react";
import {
  Mail,
  MessageCircle,
  BookOpen,
  HelpCircle,
  FileText,
  Send,
  ChevronDown,
  CheckCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Footer from "#app/components/footer";
import { LandingHeader } from "#app/components/landing/landing-header";
import { Button } from "#app/components/ui/button";
import { sendEmail } from "#app/utils/email.server";
import { useOptionalUser } from "#app/utils/user";

export const meta: MetaFunction = () => [
  { title: "Support | Supervity AI" },
  {
    name: "description",
    content:
      "Get help and support for Supervity AI. Contact us, access documentation, and find resources.",
  },
];

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const name = formData.get("name")?.toString();
  const email = formData.get("email")?.toString();
  const subject = formData.get("subject")?.toString() || "Support Request";
  const message = formData.get("message")?.toString();

  if (!name || !email || !message) {
    return json({ error: "fillRequired" }, { status: 400 });
  }

  const to = process.env.SUPPORT_INBOX_EMAIL ?? "info@supervity.ai";
  const safeName = escapeHtml(name);
  const safeEmail = escapeHtml(email);
  const safeSubject = escapeHtml(subject);
  const safeMessage = escapeHtml(message);

  try {
    const result = await sendEmail({
      to,
      replyTo: email,
      subject: `[Support Request] ${subject} from ${name}`,
      text: `Name: ${name}\nEmail: ${email}\n\nMessage:\n${message}`,
      html: `
        <h3>New Support Request</h3>
        <p><strong>Name:</strong> ${safeName}</p>
        <p><strong>Email:</strong> <a href="mailto:${safeEmail}">${safeEmail}</a></p>
        <p><strong>Subject:</strong> ${safeSubject}</p>
        <hr />
        <p><strong>Message:</strong></p>
        <p style="white-space: pre-wrap;">${safeMessage}</p>
      `,
    });

    if (result.status === "error") {
      console.error("Email send error:", result.error);
      return json({ error: "sendFailed" }, { status: 500 });
    }

    return json({ success: true });
  } catch (err) {
    console.error("Unexpected error:", err);
    return json({ error: "unexpectedError" }, { status: 500 });
  }
}

export const handle = { i18n: ["marketing", "footer", "common"] };

export default function SupportRoute() {
  const { t } = useTranslation("marketing");
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";
  const user = useOptionalUser();

  const faqs = [
    { q: t("support.faq.q1"), a: t("support.faq.a1") },
    { q: t("support.faq.q2"), a: t("support.faq.a2") },
    { q: t("support.faq.q3"), a: t("support.faq.a3") },
    { q: t("support.faq.q4"), a: t("support.faq.a4") },
  ];

  const errorKey = (actionData as { error?: string } | undefined)?.error;
  const errorMessage = errorKey ? t(`support.${errorKey}`) : null;

  const backTo = user ? "/u/alpha/agent" : "/marketplace";

  return (
    <div className="min-h-screen bg-[#FDFDFD] dark:bg-zinc-950">
      <LandingHeader backTo={backTo} />
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-[url('/img/marketplace-bg.svg')] px-4 py-20 border-b border-[#E8EBF6] dark:border-zinc-800 dark:bg-zinc-950 dark:bg-none">
        <div className="absolute inset-0 hidden bg-zinc-950/80 dark:block" />
        <div className="absolute right-0 top-0 h-full w-[45%] bg-[radial-gradient(circle_at_center,rgba(128,149,212,0.24)_0%,rgba(128,149,212,0.08)_35%,rgba(255,255,255,0)_70%)] dark:bg-[radial-gradient(circle_at_center,rgba(128,149,212,0.18)_0%,rgba(24,24,27,0)_70%)]" />
        <div className="relative container mx-auto max-w-4xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#141A42] dark:text-zinc-100 mb-6">
            {t("support.heroTitle")}
          </h1>
          <p className="text-[#5C67AA] text-lg max-w-2xl mx-auto dark:text-indigo-300">
            {t("support.heroSubtitle")}
          </p>
        </div>
      </div>

      <div className="container mx-auto max-w-5xl px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content (Forms & FAQs) */}
          <div className="lg:col-span-2 space-y-16">
            {/* Contact Form */}
            <section id="contact-us">
              <div className="bg-white rounded-2xl p-8 border border-[#E8EBF6] shadow-[0_4px_24px_rgba(83,94,164,0.04)] dark:bg-zinc-900 dark:border-zinc-700 dark:shadow-none">
                <div className="flex items-start gap-4 mb-8">
                  <div className="bg-[#535EA4]/10 p-3 rounded-xl dark:bg-indigo-950">
                    <Mail className="h-6 w-6 text-[#535EA4] dark:text-indigo-300" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-[#141A42] dark:text-zinc-100 mb-2">
                      {t("support.sendMessage")}
                    </h2>
                    <p className="text-[#5C67AA] dark:text-zinc-400">
                      {t("support.sendMessageHintPrefix")}
                      <a
                        href="mailto:info@supervity.ai"
                        className="text-[#535EA4] hover:underline font-medium dark:text-indigo-300"
                      >
                        info@supervity.ai
                      </a>
                      {t("support.sendMessageHintSuffix")}
                    </p>
                  </div>
                </div>

                {(actionData as any)?.success ? (
                  <div className="bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300 rounded-xl p-6 flex flex-col items-center text-center space-y-3">
                    <CheckCircle className="h-10 w-10 text-green-500 dark:text-green-300" />
                    <div>
                      <h3 className="font-semibold text-lg">
                        {t("support.messageSent")}
                      </h3>
                      <p className="text-green-700 dark:text-green-300 mt-1">
                        {t("support.messageSentHint")}
                      </p>
                    </div>
                  </div>
                ) : (
                  <Form method="post" className="space-y-6">
                    {(actionData as any)?.error ? (
                      <div className="bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-lg p-4 text-sm">
                        {errorMessage}
                      </div>
                    ) : null}

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label
                          htmlFor="name"
                          className="text-sm font-medium text-[#141A42] dark:text-zinc-200"
                        >
                          {t("support.fullName")}{" "}
                          <span className="text-red-500 dark:text-red-300">
                            *
                          </span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          placeholder={t("support.placeholderName")}
                          className="w-full px-4 py-3 rounded-xl border border-[#E0E4F4] focus:outline-none focus:ring-2 focus:ring-[#535EA4]/20 focus:border-[#535EA4] transition-all bg-[#FCFCFC] dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder:text-zinc-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400/20"
                        />
                      </div>
                      <div className="space-y-2">
                        <label
                          htmlFor="email"
                          className="text-sm font-medium text-[#141A42] dark:text-zinc-200"
                        >
                          {t("support.emailAddress")}{" "}
                          <span className="text-red-500 dark:text-red-300">
                            *
                          </span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          placeholder={t("support.placeholderEmail")}
                          className="w-full px-4 py-3 rounded-xl border border-[#E0E4F4] focus:outline-none focus:ring-2 focus:ring-[#535EA4]/20 focus:border-[#535EA4] transition-all bg-[#FCFCFC] dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder:text-zinc-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400/20"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="subject"
                        className="text-sm font-medium text-[#141A42] dark:text-zinc-200"
                      >
                        {t("support.subject")}{" "}
                        <span className="text-red-500 dark:text-red-300">
                          *
                        </span>
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        required
                        placeholder={t("support.placeholderSubject")}
                        className="w-full px-4 py-3 rounded-xl border border-[#E0E4F4] focus:outline-none focus:ring-2 focus:ring-[#535EA4]/20 focus:border-[#535EA4] transition-all bg-[#FCFCFC] dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder:text-zinc-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400/20"
                      />
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="message"
                        className="text-sm font-medium text-[#141A42] dark:text-zinc-200"
                      >
                        {t("support.message")}{" "}
                        <span className="text-red-500 dark:text-red-300">
                          *
                        </span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        required
                        placeholder={t("support.placeholderMessage")}
                        className="w-full px-4 py-3 rounded-xl border border-[#E0E4F4] focus:outline-none focus:ring-2 focus:ring-[#535EA4]/20 focus:border-[#535EA4] transition-all bg-[#FCFCFC] resize-y dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:placeholder:text-zinc-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400/20"
                      />
                    </div>

                    <Button
                      variant="gradient"
                      size="lg"
                      type="submit"
                      className="w-full py-6 text-base shadow-md"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? t("support.sending")
                        : t("support.sendMessageBtn")}
                      {!isSubmitting && <Send className="w-4 h-4 ml-2" />}
                    </Button>
                  </Form>
                )}
              </div>
            </section>

            {/* FAQs */}
            <section>
              <h2 className="text-2xl font-bold text-[#141A42] dark:text-zinc-100 mb-6 flex items-center gap-3">
                <HelpCircle className="text-[#535EA4] dark:text-indigo-300" />
                {t("support.faqTitle")}
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, i) => (
                  <details
                    key={i}
                    className="group bg-white rounded-xl border border-[#E8EBF6] dark:bg-zinc-900 dark:border-zinc-700 [&_summary::-webkit-details-marker]:hidden"
                  >
                    <summary className="flex items-center justify-between cursor-pointer p-6 font-medium text-[#141A42] dark:text-zinc-100">
                      {faq.q}
                      <span className="transition group-open:rotate-180 bg-[#F4F6FB] p-1.5 rounded-full text-[#5C67AA] dark:bg-zinc-800 dark:text-zinc-400">
                        <ChevronDown size={16} />
                      </span>
                    </summary>
                    <div className="px-6 pb-6 text-[#5C67AA] leading-relaxed border-t border-[#E8EBF6] pt-4 mt-2 dark:border-zinc-700 dark:text-zinc-400">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </section>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-8">
            <div className="bg-[#F4F6FB] rounded-2xl p-6 border border-[#E8EBF6] dark:bg-zinc-900 dark:border-zinc-700">
              <h3 className="font-semibold text-[#141A42] dark:text-zinc-100 mb-4 text-lg">
                {t("support.quickResources")}
              </h3>
              <div className="space-y-3">
                <Link
                  to="/docs"
                  className="flex items-center gap-3 p-3 bg-white rounded-xl border border-[#E8EBF6] hover:border-[#535EA4]/30 hover:shadow-sm transition-all group dark:bg-zinc-950 dark:border-zinc-700 dark:hover:border-indigo-500/40"
                >
                  <div className="bg-[#535EA4]/10 p-2 rounded-lg group-hover:bg-[#535EA4] transition-colors dark:bg-indigo-950">
                    <BookOpen className="h-4 w-4 text-[#535EA4] group-hover:text-white dark:text-indigo-300" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-[#141A42] dark:text-zinc-100 text-sm">
                      {t("support.docsTitle")}
                    </div>
                    <div className="text-xs text-[#5C67AA] dark:text-zinc-400">
                      {t("support.docsSubtitle")}
                    </div>
                  </div>
                </Link>
                <Link
                  to="/u/alpha/tutorials"
                  className="flex items-center gap-3 p-3 bg-white rounded-xl border border-[#E8EBF6] hover:border-[#535EA4]/30 hover:shadow-sm transition-all group dark:bg-zinc-950 dark:border-zinc-700 dark:hover:border-indigo-500/40"
                >
                  <div className="bg-[#535EA4]/10 p-2 rounded-lg group-hover:bg-[#535EA4] transition-colors dark:bg-indigo-950">
                    <FileText className="h-4 w-4 text-[#535EA4] group-hover:text-white dark:text-indigo-300" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-[#141A42] dark:text-zinc-100 text-sm">
                      {t("support.tutorialsTitle")}
                    </div>
                    <div className="text-xs text-[#5C67AA] dark:text-zinc-400">
                      {t("support.tutorialsSubtitle")}
                    </div>
                  </div>
                </Link>
                <Link
                  to={user ? "/u/community" : "/login?redirectTo=/u/community"}
                  className="flex items-center gap-3 p-3 bg-white rounded-xl border border-[#E8EBF6] hover:border-[#535EA4]/30 hover:shadow-sm transition-all group dark:bg-zinc-950 dark:border-zinc-700 dark:hover:border-indigo-500/40"
                >
                  <div className="bg-[#535EA4]/10 p-2 rounded-lg group-hover:bg-[#535EA4] transition-colors dark:bg-indigo-950">
                    <MessageCircle className="h-4 w-4 text-[#535EA4] group-hover:text-white dark:text-indigo-300" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-[#141A42] dark:text-zinc-100 text-sm">
                      {t("support.communityTitle")}
                    </div>
                    <div className="text-xs text-[#5C67AA] dark:text-zinc-400">
                      {t("support.communitySubtitle")}
                    </div>
                  </div>
                </Link>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-[#E8EBF6] dark:bg-zinc-900 dark:border-zinc-700">
              <h3 className="font-semibold text-[#141A42] dark:text-zinc-100 mb-4 text-lg">
                {t("support.legalInfo")}
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/privacy"
                    className="text-[#5C67AA] hover:text-[#535EA4] hover:underline flex items-center gap-2 text-sm dark:text-zinc-400 dark:hover:text-indigo-300"
                  >
                    <FileText className="h-4 w-4" />{" "}
                    {t("support.privacyPolicy")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/tos"
                    className="text-[#5C67AA] hover:text-[#535EA4] hover:underline flex items-center gap-2 text-sm dark:text-zinc-400 dark:hover:text-indigo-300"
                  >
                    <FileText className="h-4 w-4" />{" "}
                    {t("support.termsOfService")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/blog"
                    className="text-[#5C67AA] hover:text-[#535EA4] hover:underline flex items-center gap-2 text-sm dark:text-zinc-400 dark:hover:text-indigo-300"
                  >
                    <FileText className="h-4 w-4" /> {t("support.releaseNotes")}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
